import React, { createContext, useContext, useState, useCallback } from 'react';
import { ThemeConfig } from '@/types';
import { themePresets } from '@/themes/presets';

interface ThemeContextType {
  theme: ThemeConfig;
  setTheme: (theme: ThemeConfig) => void;
  applyToDOM: (theme: ThemeConfig) => void;
  resetToDefault: () => void;
}

const defaultTheme: ThemeConfig = {
  preset: 'classic',
  primary_color: '#8B5CF6',
  secondary_color: '#EC4899',
  accent_color: '#F59E0B',
  font_family: 'Inter',
  background: '#FFFFFF',
};

const ThemeContext = createContext<ThemeContextType>({
  theme: defaultTheme,
  setTheme: () => {},
  applyToDOM: () => {},
  resetToDefault: () => {},
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemeConfig>(defaultTheme);

  const applyToDOM = useCallback((t: ThemeConfig) => {
    const root = document.documentElement;
    root.style.setProperty('--color-primary', t.primary_color);
    root.style.setProperty('--color-secondary', t.secondary_color);
    root.style.setProperty('--color-accent', t.accent_color);
    root.style.setProperty('--color-surface', t.background || '#FFFFFF');
    root.style.setProperty('--font-display', `'${t.font_family}', serif`);
    root.style.setProperty('--font-body', `'${t.font_family}', sans-serif`);

    const isDark = isColorDark(t.background || '#FFFFFF');
    root.style.setProperty('--color-on-surface', isDark ? '#F9FAFB' : '#1F2937');
    root.style.setProperty('--color-on-primary', '#FFFFFF');
  }, []);

  const setTheme = useCallback(
    (t: ThemeConfig) => {
      setThemeState(t);
      applyToDOM(t);
    },
    [applyToDOM]
  );

  const resetToDefault = useCallback(() => {
    setThemeState(defaultTheme);
    applyToDOM(defaultTheme);
  }, [applyToDOM]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, applyToDOM, resetToDefault }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useThemeContext() {
  return useContext(ThemeContext);
}

function isColorDark(hex: string): boolean {
  const color = hex.replace('#', '');
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
}
